import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ExperimentName, getExperimentByName } from './experiments';
import { getStoredExperiments, storeVariant } from './storage';
import { getVariantFromUrl, getWeightedRandomVariant } from './utils';

type Assignments = Map<number, number>;

type ExperimentsContextValue = {
  assignExperiment: (name: ExperimentName) => void;
  setVariant: (name: ExperimentName, variant: number) => void;
  getVariant: (name: ExperimentName) => number | null;
  isInVariant: (name: ExperimentName, variant: number) => boolean;
};

const ExperimentsContext = createContext<ExperimentsContextValue | null>(null);

function initializeAssignments(): Assignments {
  const stored = getStoredExperiments();
  return new Map(stored.map((exp) => [exp.id, exp.assignedVariant]));
}

type ExperimentsProviderProps = {
  children: ReactNode;
};

export function ExperimentsProvider({ children }: ExperimentsProviderProps) {
  const [assignments, setAssignments] = useState<Assignments>(
    initializeAssignments,
  );

  const assignExperiment = useCallback((name: ExperimentName) => {
    const experiment = getExperimentByName(name);
    if (!experiment.enabled) return;

    setAssignments((current) => {
      if (current.has(experiment.id)) return current;

      const urlVariant = getVariantFromUrl(experiment);
      if (urlVariant !== null) {
        storeVariant(experiment.id, urlVariant);
        return new Map(current).set(experiment.id, urlVariant);
      }

      const randomVariant = getWeightedRandomVariant(experiment.variants);
      storeVariant(experiment.id, randomVariant);
      return new Map(current).set(experiment.id, randomVariant);
    });
  }, []);

  const getVariant = useCallback(
    (name: ExperimentName): number | null => {
      const experiment = getExperimentByName(name);
      return assignments.get(experiment.id) ?? null;
    },
    [assignments],
  );

  const isInVariant = useCallback(
    (name: ExperimentName, variant: number): boolean => {
      const experiment = getExperimentByName(name);
      return assignments.get(experiment.id) === variant;
    },
    [assignments],
  );

  const setVariant = useCallback((name: ExperimentName, variant: number) => {
    const experiment = getExperimentByName(name);
    storeVariant(experiment.id, variant);
    setAssignments((current) => new Map(current).set(experiment.id, variant));
  }, []);

  const value = useMemo(
    () => ({ assignExperiment, setVariant, getVariant, isInVariant }),
    [assignExperiment, setVariant, getVariant, isInVariant],
  );

  return (
    <ExperimentsContext.Provider value={value}>
      {children}
    </ExperimentsContext.Provider>
  );
}

export function useExperiments(): ExperimentsContextValue {
  const context = useContext(ExperimentsContext);
  if (!context) {
    throw new Error('useExperiments must be used within ExperimentsProvider');
  }
  return context;
}
